import { type ReactNode, useContext } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import type { User } from '@/@types/global';
import { RenderLogo } from '@/app/appHeader/helpers';
import { ExternalPatientContext } from '@/app/external/constants';
import { useGetDateFNSLocale } from '@/locales/helpers';
import { useGetCountry } from '@/utils/internationalization/useGetCountry';
import { parseAndFormatDate } from '@/utils/parseDate';

import { latestImagesVisitDateAtom } from '../../atoms';
import { previewDisplayDeviceAtom } from '../../multiFileViewer/imageToolbar/atoms';
import {
  patientDownloadableNotesAtom,
  patientDownloadableSelectedTreatmentsAtom,
  selectedTeethPreviewAtom,
} from '../atoms';

import { useFindingsHidden } from './hooks/useFindingsHidden';
import { TreatmentsText } from './TreatmentOptions';

interface PatientPrintoutLayoutProps {
  children: ReactNode;
  scale?: number;
}

export const PatientPrintoutLayout = ({
  children,
  scale = 1,
}: PatientPrintoutLayoutProps) => {
  const previewDisplayDevice = useAtomValue(previewDisplayDeviceAtom);
  const visitDate = useAtomValue(latestImagesVisitDateAtom);
  const clinicalNote = useAtomValue(patientDownloadableNotesAtom);
  const selectedTeethPreview = useAtomValue(selectedTeethPreviewAtom);
  const defaultSelectedTreatments = useAtomValue(
    patientDownloadableSelectedTreatmentsAtom,
  );

  const externalPatientContext = useContext(ExternalPatientContext);
  const { id: selectedPatientId } = useParams<{ id: string }>();
  const patientId = selectedPatientId ?? externalPatientContext.patientId;
  const patients = useSelector((state: any) => state.annotator?.patients) || {};
  const currentPatient = patients[patientId] || {};
  const user: User = useSelector((state: any) => state?.login?.user);
  const dateFNSLocale = useGetDateFNSLocale();
  const isCanadianPractice = useGetCountry() === 'CA';
  const isFindingsHidden = useFindingsHidden();
  const { t } = useLingui();

  const isMobile = previewDisplayDevice === 'mobile';

  return (
    <div
      className="print-container vai-w-full vai-overflow-visible vai-origin-top-left"
      style={{ transform: `scale(${scale})` }}
      data-testid="patient-printout-layout"
    >
      <style>{`@page { margin: 0.75in 0; }`}</style>
      {user.primaryPracticeName && (
        <div
          className={classNames('vai-border-b vai-border-slate-200 vai-mb-4', {
            'vai-px-8 vai-pb-4': !isMobile,
            'vai-px-4 vai-pt-6 vai-pb-3': isMobile,
          })}
        >
          <p
            className={classNames(
              'vai-font-inter vai-font-semibold vai-text-[#061E3E]',
              { 'vai-text-3xl': !isMobile, 'vai-text-2xl': isMobile },
            )}
          >
            {user.primaryPracticeName}
          </p>
        </div>
      )}
      <div
        className={classNames('vai-pb-8', {
          'vai-px-10': !isMobile,
          'vai-px-4': isMobile,
        })}
      >
        <RenderLogo color="#061E3E" />

        {!isMobile && currentPatient.name && (
          <p className="vai-font-inter vai-text-sm vai-mb-4 vai-uppercase vai-my-3">
            {currentPatient.name}
          </p>
        )}

        <div className="vai-flex vai-justify-between vai-items-center vai-gap-2 vai-my-3">
          <p
            className={classNames('vai-font-inter vai-font-semibold', {
              'vai-text-2xl': !isMobile,
              'vai-text-xl': isMobile,
            })}
          >
            <Trans>Patient Report</Trans>
          </p>
          {visitDate && (
            <p className="vai-font-inter vai-font-semibold">
              {parseAndFormatDate(
                visitDate.toISOString(),
                'MMM. d, yyyy',
                dateFNSLocale,
              )}
            </p>
          )}
        </div>
        <p
          className={classNames('vai-font-inter vai-text-zinc-600 vai-mb-4', {
            'vai-text-[12px]': isMobile,
          })}
        >
          <Trans>Your dental practice has analyzed your x-rays with</Trans>{' '}
          {isCanadianPractice ? t`clinically validated` : t`FDA-cleared`}{' '}
          <Trans>
            AI algorithms from VideaAI. This is a copy of the findings for your
            records. These findings are not direct diagnoses.
          </Trans>
        </p>

        {clinicalNote && (
          <>
            <p className="vai-font-inter vai-font-semibold vai-text-zinc-900 vai-mb-4">
              <Trans>Clinician Note</Trans>
            </p>
            <p className="vai-font-inter vai-mb-4">{clinicalNote}</p>
          </>
        )}

        {children}

        {/* Disclaimer section */}
        {(!!isFindingsHidden || !!selectedTeethPreview.length) && (
          <p className="vai-font-inter vai-text-sm vai-text-zinc-600 vai-my-4">
            <Trans>
              * Note: some analyzed findings may not be displayed in image and
              summary above
            </Trans>
          </p>
        )}

        {/* Treatments section */}
        {!!defaultSelectedTreatments.length && (
          <div>
            <p className="vai-font-inter vai-font-semibold vai-text-zinc-900 vai-my-4">
              <Trans>Treatment</Trans>
            </p>

            <div className="vai-flex vai-flex-col vai-gap-6">
              {defaultSelectedTreatments.map((treatment) => (
                <TreatmentsText
                  className={classNames('vai-break-inside-avoid', {
                    '[&:nth-of-type(even)]:vai-flex-row-reverse': !isMobile,
                    'vai-flex-col': isMobile,
                  })}
                  key={treatment}
                  showDiagram={true}
                  treatment={treatment}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
